import React from 'react';
import { 
    Drawer, List, ListItemButton, ListItemIcon, ListItemText,
    Divider, Box, Typography, Avatar, Chip
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import LogoutIcon from '@mui/icons-material/Logout';
import ConstructionIcon from '@mui/icons-material/Construction';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { authService } from '../../services/auth';

const Sidebar = ({ open, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout failed:', error);
            window.location.href = '/login';
        }
    };

    const menuItems = [
        { text: 'Onboarding', icon: <GroupAddIcon />, path: '/onboarding', badge: 'Core' },
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'Parameters Settings', icon: <SettingsIcon />, path: '/settings' },
        { text: 'Testing Console', icon: <ConstructionIcon />, path: '/testing' },
    ];

    return (
        <Drawer
            open={open}
            onClose={onClose}
            sx={{
                width: 280,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: 280,
                    boxSizing: 'border-box',
                    backgroundColor: '#FFFFFF',
                    borderRight: '1px solid #E2E8F0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                },
            }}
        >
            <Box>
                {/* Header inside Sidebar */}
                <Box sx={{ p: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                        sx={{
                            width: 38,
                            height: 38,
                            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                            fontWeight: 800,
                            fontSize: '0.95rem'
                        }}
                    >
                        GC
                    </Avatar>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>
                                GET·CODE
                            </Typography>
                            <Chip label="PRO" size="small" sx={{ height: 18, fontSize: '0.625rem', bgcolor: 'primary.main', color: '#fff', fontWeight: 800 }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                            Admin Operations
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mx: 2, mb: 1.5 }} />

                <List sx={{ px: 1.5 }}>
                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <ListItemButton
                                key={item.text}
                                selected={isActive}
                                onClick={() => {
                                    navigate(item.path);
                                    onClose();
                                }}
                                sx={{
                                    borderRadius: '10px',
                                    mb: 0.75,
                                    py: 1.2,
                                    px: 1.5,
                                    transition: 'all 0.15s ease',
                                    backgroundColor: isActive ? 'rgba(79, 70, 229, 0.08)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: isActive ? 'rgba(79, 70, 229, 0.12)' : '#F8FAFC',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(79, 70, 229, 0.09)',
                                    }
                                }}
                            >
                                <ListItemIcon 
                                    sx={{ 
                                        minWidth: 38,
                                        color: isActive ? 'primary.main' : '#64748B'
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                    primary={item.text} 
                                    primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                        fontWeight: isActive ? 700 : 500,
                                        color: isActive ? 'primary.main' : '#334155'
                                    }}
                                />
                                {item.badge && (
                                    <Chip 
                                        label={item.badge} 
                                        size="small" 
                                        sx={{ 
                                            height: 20, 
                                            fontSize: '0.65rem', 
                                            bgcolor: isActive ? 'primary.main' : '#F1F5F9',
                                            color: isActive ? '#fff' : '#64748B',
                                            fontWeight: 700
                                        }} 
                                    />
                                )}
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>

            {/* Bottom info & Logout */}
            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
                <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <AutoAwesomeIcon sx={{ fontSize: 16, color: '#4F46E5' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#0F172A' }}>
                            Scout Automation
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '0.72rem' }}>
                        In-house OpenAI & Telegram Bot Agent nested in backend.
                    </Typography>
                </Box>

                <ListItemButton
                    onClick={handleLogout}
                    sx={{
                        borderRadius: '10px',
                        py: 1,
                        px: 1.5,
                        color: '#DC2626',
                        '&:hover': {
                            backgroundColor: '#FEE2E2',
                        }
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 38, color: '#DC2626' }}>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Sign Out" 
                        primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: 600
                        }}
                    />
                </ListItemButton>
            </Box>
        </Drawer>
    );
};

export default Sidebar;